/**
 * Outsource Partner Portal — Login.
 * Outsource partners live in `outsource_partners` and authenticate via
 * their own JWT issued by `/api/outsource/auth/login`. Token stored under
 * `outsource_token`.
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Handshake, Loader2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function OutsourceLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/api/outsource/auth/login`, { email, password });
      localStorage.setItem('outsource_token', r.data.token);
      localStorage.setItem('outsource_profile', JSON.stringify(r.data.partner));
      toast.success(`Welcome, ${r.data.partner.name}`);
      navigate('/outsource/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="outsource-login-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 text-amber-700">
            <Handshake className="w-7 h-7" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Outsource Partner Portal</h1>
          <p className="text-sm text-gray-500">Sign in to review loan cases referred to your agency.</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              data-testid="outsource-login-email"
            />
          </div>
          <div>
            <Label>Password</Label>
            <Input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              data-testid="outsource-login-password"
            />
          </div>
          <Button type="submit" disabled={busy} className="w-full" data-testid="outsource-login-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign in'}
          </Button>
        </form>
        <p className="text-xs text-center text-gray-500">
          First time?{' '}
          <Link to="/outsource/setup" className="text-amber-600 hover:underline">
            Activate with invite token
          </Link>
        </p>
      </div>
    </div>
  );
}
